import React, { useMemo } from "react";
import { Autocomplete, Box, MenuItem, TextField, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { isChatBotUser } from "../utils/assigneeTransferField";
import { fallbackForDisplay } from "../utils/loggedUsersFallback";

const DEST_QUEUE = "fila_humano";
const DEST_USERS = "humano";
const DEST_AGENT = "agente_ia";
const DEST_CHATBOT = "chatbot";

function userLabel(user) {
  return user?.nome ?? user?.name ?? `Usuário #${user?.id}`;
}

/**
 * Destino quando ninguém da lista principal está logado.
 * Sem escolha gravada, a interface mostra a fila (mesmo failover da execução).
 */
export default function LoggedUsersFallbackField({
  fallback,
  users = [],
  agents = [],
  disabled = false,
  onChange,
}) {
  const { t } = useTranslation("settings");
  const value = fallbackForDisplay(fallback);

  const humanUsers = useMemo(
    () => (users || []).filter((user) => user && !isChatBotUser(user)),
    [users]
  );
  const chatbotUsers = useMemo(
    () => (users || []).filter((user) => isChatBotUser(user)),
    [users]
  );

  const selectedUsers = useMemo(() => {
    const ids = String(value.target_id || "")
      .split(",")
      .map((id) => Number(id.trim()))
      .filter((id) => Number.isFinite(id) && id > 0);
    return humanUsers.filter((user) => ids.includes(Number(user.id)));
  }, [humanUsers, value.target_id]);

  const selectedAgent = useMemo(() => {
    if (value.dest !== DEST_AGENT || !value.target_id) return null;
    return (
      (agents || []).find((agent) => String(agent.id ?? agent.id_agente ?? agent.agentId) === value.target_id) ||
      null
    );
  }, [agents, value.dest, value.target_id]);

  const selectedChatbot = useMemo(() => {
    if (value.dest !== DEST_CHATBOT || !value.target_id) return null;
    return chatbotUsers.find((user) => String(user.id) === String(value.target_id)) || null;
  }, [chatbotUsers, value.dest, value.target_id]);

  const emit = (dest, targetId) => {
    onChange?.({
      dest,
      target_id: dest === DEST_QUEUE ? "" : String(targetId || "").trim(),
    });
  };

  return (
    <Box sx={{ pl: 0.5, pt: 1 }}>
      <TextField
        select
        size="small"
        fullWidth
        margin="dense"
        disabled={disabled}
        label={t("agentsListPage.loggedUsersFallbackLabel", {
          defaultValue: "Se ninguém estiver logado",
        })}
        helperText={t("agentsListPage.loggedUsersFallbackHelper", {
          defaultValue: "Sem esta escolha, a solicitação vai para a fila.",
        })}
        value={value.dest}
        onChange={(e) => emit(e.target.value, "")}
      >
        <MenuItem value={DEST_QUEUE}>
          {t("agentsListPage.loggedUsersFallbackQueue", { defaultValue: "Enviar para a fila" })}
        </MenuItem>
        <MenuItem value={DEST_USERS}>
          {t("agentsListPage.loggedUsersFallbackUsers", {
            defaultValue: "Distribuir para outros usuários",
          })}
        </MenuItem>
        <MenuItem value={DEST_AGENT}>
          {t("agentsListPage.loggedUsersFallbackAgent", { defaultValue: "Agente de IA" })}
        </MenuItem>
        <MenuItem value={DEST_CHATBOT}>
          {t("agentsListPage.loggedUsersFallbackChatbot", { defaultValue: "Chatbot" })}
        </MenuItem>
      </TextField>

      {value.dest === DEST_USERS ? (
        <Autocomplete
          multiple
          disabled={disabled}
          options={humanUsers}
          value={selectedUsers}
          getOptionLabel={userLabel}
          isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
          onChange={(_, next) => {
            const ids = (next || [])
              .map((user) => Number(user.id))
              .filter((id) => Number.isFinite(id) && id > 0);
            emit(DEST_USERS, ids.join(","));
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              margin="dense"
              label={t("agentsListPage.loggedUsersFallbackPickUsers", {
                defaultValue: "Usuários",
              })}
            />
          )}
        />
      ) : null}

      {value.dest === DEST_AGENT ? (
        <Autocomplete
          disabled={disabled}
          options={agents || []}
          value={selectedAgent}
          getOptionLabel={(agent) => agent?.name ?? agent?.nome ?? `Agente #${agent?.id}`}
          isOptionEqualToValue={(a, b) =>
            String(a?.id ?? a?.id_agente ?? "") === String(b?.id ?? b?.id_agente ?? "")
          }
          onChange={(_, next) => {
            const id = next?.id ?? next?.id_agente ?? next?.agentId ?? "";
            emit(DEST_AGENT, id);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              margin="dense"
              label={t("agentsListPage.loggedUsersFallbackPickAgent", {
                defaultValue: "Agente de IA",
              })}
            />
          )}
        />
      ) : null}

      {value.dest === DEST_CHATBOT ? (
        <Autocomplete
          disabled={disabled}
          options={chatbotUsers}
          value={selectedChatbot}
          getOptionLabel={(user) => {
            const name = userLabel(user);
            return name.endsWith("(ChatBot)") ? name : `${name} (ChatBot)`;
          }}
          isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
          onChange={(_, next) => emit(DEST_CHATBOT, next?.id || "")}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              margin="dense"
              label={t("agentsListPage.loggedUsersFallbackPickChatbot", {
                defaultValue: "Chatbot",
              })}
            />
          )}
        />
      ) : null}

      {value.dest === DEST_USERS && !value.target_id ? (
        <Typography variant="caption" color="text.secondary" display="block">
          {t("agentsListPage.loggedUsersFallbackUsersEmpty", {
            defaultValue: "Escolha os usuários. Sem escolha, a solicitação vai para a fila.",
          })}
        </Typography>
      ) : null}
    </Box>
  );
}
